import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { RightOutlined, LeftOutlined, DownOutlined } from "@ant-design/icons";
import { Dropdown, Tooltip } from "antd";

/**
 * Match a statusHistory entry to a node — returns the entry if its newStatus.id
 * equals node.id OR if any ancestor in the newStatus.parentStatus chain equals
 * node.id. Mirrors the match logic in OpportunityCascaders.getStatusIconDisplay.
 */
const findMatchingHistoryEntry = (statusHistory, nodeId) => {
  if (!nodeId) return null;
  return (
    (statusHistory || []).find(entry => {
      if (!entry?.newStatus) return false;
      if (+entry.newStatus.id === +nodeId) return true;
      let { parentStatus } = entry.newStatus;
      while (parentStatus) {
        if (+parentStatus.id === +nodeId) return true;
        ({ parentStatus } = parentStatus);
      }
      return false;
    }) || null
  );
};

/**
 * Compute the state of each status node using the same rules as
 * OpportunityCascaders.getStatusIconDisplay:
 *  - "pending"   : no history entry matches this node (Upcoming)
 *  - "skipped"   : matched entry has isTransferred === true
 *  - "current"   : matched AND (this node is the topmost parent of the current
 *                  status's history entry, or the matched entry IS the current)
 *  - "completed" : matched, not transferred, not current
 *
 * Unlike the prior tree-order heuristic, "skipped" is driven by the explicit
 * isTransferred flag on the status history entry — not by tree position.
 */
const computeTimelineStates = (statusTree, currentStatusId, statusHistory) => {
  if (!statusTree?.length) return [];
  // Sort by createTs DESC so `.find()` below returns the MOST RECENT matching
  // entry for a given tree node. The server does not guarantee any ordering on
  // statusHistory, so without this sort a stale earlier entry can mask a newer
  // one — e.g., a parent touched first via a child (isTransferred=false) and
  // later skipped as a direct transfer (isTransferred=true) resolves to
  // "completed" (the stale false entry) instead of "skipped" (the latest true).
  const fullHistory = [...(statusHistory || [])].sort((a, b) => {
    const ta = new Date(a?.createTs || 0).getTime();
    const tb = new Date(b?.createTs || 0).getTime();
    return tb - ta;
  });

  // Build a map of statusId -> index of its top-level parent in the (sorted) tree.
  // statusTree is already sorted ascending by `order` in normalizeStatusTree, so
  // the array index IS the canonical position of each status in the flow.
  const topParentIndexById = new Map();
  statusTree.forEach((parent, idx) => {
    topParentIndexById.set(String(parent.id), idx);
    (parent.children || []).forEach(child => {
      topParentIndexById.set(String(child.id), idx);
    });
  });

  // Drop history entries whose top-level parent comes AFTER the current status.
  // This handles backwards transitions: a status that was previously touched but
  // is now ahead of the current position should render as "pending" (upcoming),
  // not "completed"/"skipped". If the current status isn't in the tree, we can't
  // safely filter, so fall back to the raw history.
  const currentTopIdx = topParentIndexById.has(String(currentStatusId))
    ? topParentIndexById.get(String(currentStatusId))
    : null;

  const history =
    currentTopIdx === null
      ? fullHistory
      : fullHistory.filter(entry => {
          if (!entry?.newStatus) return false;
          let topParent = entry.newStatus;
          while (topParent.parentStatus) topParent = topParent.parentStatus;
          const idx = topParentIndexById.get(String(topParent.id));
          // Unknown status (not in the current tree) — keep it; we don't have
          // enough info to drop it safely.
          if (idx === undefined) return true;
          return idx <= currentTopIdx;
        });

  // Find the history entry whose newStatus is the current status, then walk up
  // the parentStatus chain to the topmost parent — used for "current" detection.
  const currentStatusEntry = history.find(e => +e.newStatus?.id === +currentStatusId);
  let currentTopParentId = null;
  if (currentStatusEntry?.newStatus) {
    let top = currentStatusEntry.newStatus;
    while (top.parentStatus) top = top.parentStatus;
    currentTopParentId = +top.id;
  }

  // Returns { state, substatusName } for a node.
  // substatusName is the specific child/leaf status within this node (if any)
  // that was visited or is currently active — used to render tooltips like
  // "Parent > Child" (e.g. "Sold > Delivered").
  const getNodeMeta = node => {
    const matched = findMatchingHistoryEntry(history, node.id);

    const subName = entry =>
      entry?.newStatus && +entry.newStatus.id !== +node.id ? entry.newStatus.name : null;

    if (!matched) return { state: "pending", substatusName: null };
    if (matched.isTransferred === true) {
      return { state: "skipped", substatusName: subName(matched) };
    }
    if (!currentStatusEntry?.newStatus) return { state: "pending", substatusName: null };
    if (currentTopParentId !== null && +currentTopParentId === +node.id) {
      // This node is the current top-parent — show the current sub-status name
      return { state: "current", substatusName: subName(currentStatusEntry) };
    }
    if (+matched.newStatus.id === +currentStatusId) {
      return { state: "current", substatusName: subName(matched) };
    }
    return { state: "completed", substatusName: subName(matched) };
  };

  return statusTree.map(parent => {
    const { state, substatusName } = getNodeMeta(parent);
    return {
      ...parent,
      state,
      substatusName,
      children: (parent.children || []).map(child => {
        const childMeta = getNodeMeta(child);
        return { ...child, state: childMeta.state };
      }),
    };
  });
};

/**
 * Color map for each status state:
 * - completed: Green
 * - skipped: Orange/Gold
 * - current: Blue
 * - pending: Grey
 */
const STATE_COLORS = {
  completed: { bg: "#D9F7BE", text: "#135200" },
  skipped: { bg: "#FFF1B8", text: "#613400" },
  current: { bg: "#BAE0FF", text: "#003A8C" },
  pending: { bg: "#E7E7E7", text: "#595959" },
};

/**
 * SVG chevron-shaped status segment.
 * Parents with children show a dropdown on click.
 */
const ChevronSegment = ({
  status,
  isFirst,
  currentStatusId,
  onStatusChange,
  allowParentSelection = false,
}) => {
  const colors = STATE_COLORS[status.state] || STATE_COLORS.pending;
  const { bg: bgColor, text: textColor } = colors;
  const hasChildren = status.children?.length > 0;
  const isClickable = typeof onStatusChange === "function";
  const truncatedName =
    status.name?.length > 18 ? `${status.name.substring(0, 16)}...` : status.name;
  // Tooltip shows "Parent > Substatus" when a specific sub-status is relevant
  // (e.g. "Sold > Delivered"); otherwise just the parent name.
  const tooltipTitle = status.substatusName
    ? `${status.name} > ${status.substatusName}`
    : status.name;

  // When allowParentSelection is on, the chevron body becomes a click target that
  // selects the parent itself; the dropdown (children menu) is attached only to
  // the small DownOutlined caret. Without the flag, the entire chevron opens the
  // children dropdown — original behavior.
  const bodyClickSelects = isClickable && (!hasChildren || allowParentSelection);

  const handleBodyClick = () => {
    if (!bodyClickSelects) return;
    onStatusChange(status.id);
  };

  const chevronSvg = (
    <svg
      viewBox="0 0 160 24"
      preserveAspectRatio="none"
      style={{ width: "100%", height: 24, minWidth: 110 }}
    >
      <path
        d={
          isFirst
            ? "M0,0 L148,0 L160,12 L148,24 L0,24 Z"
            : "M0,0 L148,0 L160,12 L148,24 L0,24 L12,12 Z"
        }
        fill={bgColor}
      />
    </svg>
  );

  const label = (
    <span
      className="absolute inset-0 flex items-center justify-center text-xs font-medium whitespace-nowrap pointer-events-none"
      style={{
        color: textColor,
        paddingLeft: isFirst ? 10 : 16,
        paddingRight: hasChildren ? 22 : 12,
      }}
    >
      {truncatedName}
    </span>
  );

  // Inline caret — only when the chevron itself is the dropdown trigger (i.e.
  // parent selection is disabled). When allowParentSelection is on, the caret is
  // rendered outside this element as its own Dropdown trigger.
  const caret =
    hasChildren && !allowParentSelection ? (
      <DownOutlined
        className="absolute pointer-events-none"
        style={{
          fontSize: 8,
          color: textColor,
          right: 14,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      />
    ) : null;

  const chevronContent = (
    <div
      className="flex items-center flex-shrink-0 relative"
      style={{
        height: 24,
        marginRight: -1,
        cursor: bodyClickSelects || hasChildren ? "pointer" : undefined,
      }}
      onClick={handleBodyClick}
      role={bodyClickSelects ? "button" : undefined}
      tabIndex={bodyClickSelects ? 0 : undefined}
    >
      {chevronSvg}
      {label}
      {caret}
    </div>
  );

  // If parent has children, build the children dropdown
  if (hasChildren) {
    const menuItems = status.children.map(child => {
      const isChildCurrent = String(child.id) === String(currentStatusId);
      return {
        key: child.id,
        label: (
          <span
            className="flex items-center gap-2 py-0.5 text-xs"
            style={{
              color: isChildCurrent ? "#003A8C" : undefined,
              fontWeight: isChildCurrent ? 600 : 400,
              backgroundColor: isChildCurrent ? "#BAE0FF" : undefined,
              margin: isChildCurrent ? "-5px -12px" : undefined,
              padding: isChildCurrent ? "5px 12px" : undefined,
              borderRadius: isChildCurrent ? 4 : undefined,
            }}
          >
            {child.name}
          </span>
        ),
      };
    });

    const dropdownProps = {
      menu: {
        items: menuItems,
        onClick: isClickable ? ({ key }) => onStatusChange(key) : undefined,
      },
      trigger: ["click"],
      placement: "bottomRight",
    };

    // When allowParentSelection, the chevron body selects the parent (via
    // handleBodyClick) and the dropdown is attached only to the caret. Otherwise,
    // the entire chevron opens the children dropdown — original behavior.
    if (allowParentSelection) {
      return (
        <Tooltip title={tooltipTitle}>
          <div className="relative flex items-center flex-shrink-0">
            {chevronContent}
            <Dropdown {...dropdownProps}>
              <DownOutlined
                className="absolute cursor-pointer"
                style={{
                  fontSize: 8,
                  color: textColor,
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                }}
              />
            </Dropdown>
          </div>
        </Tooltip>
      );
    }

    return (
      <Dropdown {...dropdownProps}>
        <Tooltip title={tooltipTitle}>{chevronContent}</Tooltip>
      </Dropdown>
    );
  }

  return <Tooltip title={tooltipTitle}>{chevronContent}</Tooltip>;
};

const TimelineList = ({
  statusTree,
  statusHistory,
  currentStatusId,
  // Identity of the opportunity the track is showing. Only used to re-trigger
  // the auto-scroll when the lead/opportunity changes but lands on the same
  // status the previous one was on (the user may have scrolled away by hand).
  opportunityId,
  onStatusChange,
  allowParentSelection = false,
}) => {
  const scrollRef = useRef(null);
  // False until the track has been auto-positioned once; the first positioning
  // is instant (no animation on open), later ones animate.
  const hasAutoScrolledRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const timelineStatuses = useMemo(
    () => computeTimelineStates(statusTree, currentStatusId, statusHistory),
    [statusTree, currentStatusId, statusHistory]
  );

  // Index of the "current" chevron in the track, or -1 when the lead/opportunity
  // has no resolvable current status. Kept as a primitive so the auto-scroll
  // effect below only re-runs when the target actually moves — `statusHistory`
  // is a fresh `[]` on every parent render when the opp has no history, so
  // depending on `timelineStatuses` directly would re-fire (and fight the user's
  // manual scrolling) on every render.
  const currentIndex = useMemo(() => {
    const flagged = timelineStatuses.findIndex(s => s.state === "current");
    if (flagged >= 0) return flagged;
    // No node resolved to "current" — this opportunity has no matching status
    // history (history creation can be skipped per location). Fall back to the
    // status id's own position in the tree so the bar still lands on it.
    if (!currentStatusId) return -1;
    return timelineStatuses.findIndex(
      s =>
        String(s.id) === String(currentStatusId) ||
        (s.children || []).some(child => String(child.id) === String(currentStatusId))
    );
  }, [timelineStatuses, currentStatusId]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Auto-scroll the track so the current status is visible. Runs whenever the
  // current status moves — including when switching to a lead whose status is
  // EARLIER than the previous one, which must scroll the track back towards the
  // start. The track keeps its scroll position across lead switches (the node
  // never unmounts), so a missing update leaves the previous lead's offset.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const frame = requestAnimationFrame(() => {
      const behavior = hasAutoScrolledRef.current ? "smooth" : "auto";
      hasAutoScrolledRef.current = true;

      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      // No current status to aim at (e.g. an opp with no status history) —
      // reset to the start rather than leaving the previous lead's offset.
      let left = 0;

      if (currentIndex >= 0 && maxScroll > 0) {
        const segment = el.children[currentIndex];
        if (segment) {
          // Centre the current chevron in the visible track, clamped to the ends
          // so the first/last statuses sit flush instead of half-scrolled.
          // Measured off bounding rects (not offsetLeft) so it stays correct
          // regardless of which ancestor is the offsetParent.
          const trackRect = el.getBoundingClientRect();
          const segmentRect = segment.getBoundingClientRect();
          const offsetLeft = el.scrollLeft + (segmentRect.left - trackRect.left);
          left = offsetLeft - (el.clientWidth - segmentRect.width) / 2;
        }
      }

      el.scrollTo({ left: Math.min(Math.max(left, 0), maxScroll), behavior });
      updateScrollState();
    });

    return () => cancelAnimationFrame(frame);
  }, [currentIndex, currentStatusId, opportunityId, timelineStatuses.length, updateScrollState]);

  const scroll = useCallback(
    dir => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: dir * 200, behavior: "smooth" });
      setTimeout(updateScrollState, 350);
    },
    [updateScrollState]
  );

  if (!timelineStatuses.length) {
    return (
      <div className="py-2 px-4 text-xs text-grey-400" data-testid="timeline-empty">
        No statuses available
      </div>
    );
  }

  return (
    <div className="flex items-center w-full px-1 py-1.5" data-testid="timeline-list">
      {/* Left scroll arrow — always visible, square */}
      <button
        type="button"
        onClick={() => scroll(-1)}
        disabled={!canScrollLeft}
        className={`flex items-center justify-center w-6 h-6 rounded flex-shrink-0 z-10 mr-1 border transition-colors ${
          canScrollLeft
            ? "bg-grey-100 border-none cursor-pointer hover:bg-grey-200 text-grey-600"
            : "bg-grey-50 border-none cursor-default text-grey-300"
        }`}
        aria-label="Scroll left"
      >
        <LeftOutlined style={{ fontSize: 10 }} />
      </button>

      {/* Scrollable chevron track */}
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex items-center overflow-x-auto flex-1 timeline-scroll"
      >
        {timelineStatuses.map((status, idx) => (
          <ChevronSegment
            key={status.id || idx}
            status={status}
            isFirst={idx === 0}
            isLast={idx === timelineStatuses.length - 1}
            currentStatusId={currentStatusId}
            onStatusChange={onStatusChange}
            allowParentSelection={allowParentSelection}
          />
        ))}
      </div>

      {/* Right scroll arrow — always visible, square */}
      <button
        type="button"
        onClick={() => scroll(1)}
        disabled={!canScrollRight}
        className={`flex items-center justify-center w-6 h-6 rounded flex-shrink-0 z-10 ml-1 border transition-colors ${
          canScrollRight
            ? "bg-grey-100 border-none cursor-pointer hover:bg-grey-200 text-grey-600"
            : "bg-grey-50 border-none cursor-default text-grey-300"
        }`}
        aria-label="Scroll right"
      >
        <RightOutlined style={{ fontSize: 10 }} />
      </button>
    </div>
  );
};

export default TimelineList;
